import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { CalendarDays, Clock, Pencil, Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { atelierService } from '../../services/api';
import AddAtelierDialog from '../../components/AddAtelierDialog';
import EditAtelierDialog from '../../components/EditAtelierDialog';

const AdminAteliersView = ({ 
  ateliers, 
  users, 
  editingAtelier, 
  setEditingAtelier, 
  onRefresh 
}) => {
  const [deleteDialog, setDeleteDialog] = useState({ open: false, atelier: null });
  const [deleting, setDeleting] = useState(false);

  const handleDeleteAtelier = async () => {
    if (!deleteDialog.atelier) return;
    
    setDeleting(true);
    try {
      await atelierService.delete(deleteDialog.atelier.id);
      toast.success(`Atelier "${deleteDialog.atelier.titre}" supprimé avec succès`);
      setDeleteDialog({ open: false, atelier: null });
      onRefresh();
    } catch (error) {
      console.error('Erreur suppression:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <div className="flex flex-wrap justify-between items-center gap-3 mb-6">
        <h2 className="text-lg sm:text-xl font-bold text-slate-900">Gestion des Ateliers & Événements</h2>
        <AddAtelierDialog onSuccess={onRefresh} />
      </div>

      {ateliers.length === 0 ? (
        <Card className="bg-white border-slate-200 rounded-3xl p-6 shadow-sm">
          <CardContent className="text-center py-12">
            <CalendarDays className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">Aucun atelier planifié</p>
            <p className="text-sm text-slate-400 mt-2">Créez un nouvel atelier pour commencer</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {ateliers.map((atelier) => (
            <AtelierCard 
              key={atelier.id} 
              atelier={atelier} 
              users={users}
              onEdit={() => setEditingAtelier(atelier)}
              onDelete={() => setDeleteDialog({ open: true, atelier })}
            />
          ))}
        </div>
      )}

      {/* Dialog d'édition */}
      {editingAtelier && (
        <EditAtelierDialog 
          atelier={editingAtelier}
          open={!!editingAtelier}
          onOpenChange={(open) => !open && setEditingAtelier(null)}
          onSuccess={onRefresh}
        />
      )}

      {/* Dialog de confirmation de suppression */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog({ open: false, atelier: null })}>
        <DialogContent className="sm:max-w-md rounded-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Supprimer l'atelier
            </DialogTitle>
            <DialogDescription>
              Cette action est irréversible. Toutes les données associées seront supprimées.
            </DialogDescription>
          </DialogHeader>
          
          {deleteDialog.atelier && (
            <div className="py-4">
              <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                <p className="font-semibold text-slate-900">{deleteDialog.atelier.titre}</p>
                <p className="text-sm text-slate-600 mt-1">
                  {deleteDialog.atelier.date} • {deleteDialog.atelier.heure_debut} - {deleteDialog.atelier.heure_fin}
                </p>
                <p className="text-sm text-slate-500 mt-2">
                  {deleteDialog.atelier.participants?.length || 0} participant(s) inscrit(s)
                </p>
              </div>
              <p className="text-sm text-slate-600 mt-4">
                <strong>Suppression en cascade :</strong> L'atelier, les émargements, signatures et notifications liées seront définitivement supprimés.
              </p>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => setDeleteDialog({ open: false, atelier: null })}
              className="rounded-xl"
              disabled={deleting}
            >
              Annuler
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteAtelier}
              className="rounded-xl"
              disabled={deleting}
              data-testid="confirm-delete-atelier"
            >
              {deleting ? 'Suppression...' : 'Supprimer définitivement'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

// Sous-composant carte atelier
const AtelierCard = ({ atelier, users, onEdit, onDelete }) => {
  const animateursList = users.filter(u => atelier.animateurs_ids?.includes(u.id));

  return (
    <Card className="bg-white border-slate-200 rounded-3xl shadow-sm overflow-hidden">
      <CardHeader className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">
          <div className="flex-1 min-w-0">
            <CardTitle className="text-base sm:text-xl font-bold mb-1 sm:mb-2 leading-tight">{atelier.titre}</CardTitle>
            <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-blue-100 text-xs sm:text-sm">
              <span className="flex items-center gap-1">
                <CalendarDays className="w-3 h-3 sm:w-4 sm:h-4" />
                {atelier.date}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3 sm:w-4 sm:h-4" />
                {atelier.heure_debut} - {atelier.heure_fin}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button
              size="sm"
              variant="secondary"
              onClick={onEdit}
              className="bg-white/20 hover:bg-white/30 text-white rounded-xl text-xs sm:text-sm px-2 sm:px-3"
              data-testid={`edit-atelier-${atelier.id}`}
            >
              <Pencil className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
              <span className="hidden sm:inline">Modifier</span>
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={onDelete}
              className="bg-red-500/80 hover:bg-red-600 text-white rounded-xl"
              data-testid={`delete-atelier-${atelier.id}`}
            >
              <Trash2 className="w-3 h-3 sm:w-4 sm:h-4" />
            </Button>
            <div className="px-2 py-1 bg-white/20 rounded-full text-xs font-bold hidden sm:block">
              {atelier.type_atelier}
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-6">
        {/* Intervenants */}
        <div className="mb-4">
          <p className="text-sm font-bold text-slate-700 mb-2">Intervenant(s)</p>
          <div className="flex flex-wrap gap-2">
            {animateursList.map((a) => (
              <span key={a.id} className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-bold">
                {a.prenom} {a.nom}
              </span>
            ))}
          </div>
        </div>

        {/* Participants */}
        <div>
          <p className="text-sm font-bold text-slate-700 mb-2">
            Participants ({atelier.participants?.length || 0})
          </p>
          <div className="space-y-1 max-h-[150px] overflow-y-auto">
            {atelier.participants?.filter(p => p.prenom || p.nom).map((p) => {
              const isPresent = p.statut_presence === 'present' || p.present === true;
              const isNonDefini = p.statut_presence === 'non_defini' || p.present === null || (p.present !== true && p.present !== false && !p.statut_presence);
              return (
                <div key={p.id} className={`flex items-center justify-between p-2 rounded-lg text-sm ${
                  isPresent ? 'bg-emerald-50' : isNonDefini ? 'bg-slate-50' : 'bg-red-50'
                }`}>
                  <span>{p.prenom} {p.nom}</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                    isPresent ? 'bg-emerald-100 text-emerald-700' : isNonDefini ? 'bg-slate-200 text-slate-600' : 'bg-red-100 text-red-700'
                  }`}>
                    {isPresent ? 'Présent' : isNonDefini ? 'Non défini' : 'Absent'}
                  </span>
                </div>
              );
            })}
            {atelier.participants?.filter(p => !p.prenom && !p.nom).length > 0 && (
              <p className="text-xs text-slate-400 italic">
                + {atelier.participants.filter(p => !p.prenom && !p.nom).length} participant(s) non identifié(s)
              </p>
            )}
          </div>
        </div>

        {/* Invités */}
        {atelier.invites?.length > 0 && (
          <div className="mt-4 pt-4 border-t border-slate-100">
            <p className="text-sm font-bold text-slate-700 mb-2 flex items-center gap-2">
              <span>Invités ({atelier.invites.length})</span>
            </p>
            <div className="space-y-1 max-h-[120px] overflow-y-auto">
              {atelier.invites.map((invite) => (
                <div key={invite.id} className={`flex items-center justify-between p-2 rounded-lg text-sm ${
                  invite.statut_confirmation === 'confirme' ? 'bg-emerald-50' :
                  invite.statut_confirmation === 'decline' ? 'bg-red-50' : 'bg-purple-50'
                }`}>
                  <div className="flex items-center gap-2">
                    <span>{invite.prenom} {invite.nom}</span>
                    <span className={`text-xs px-1.5 py-0.5 rounded ${
                      invite.role === 'financeur' ? 'bg-purple-100 text-purple-700' :
                      invite.role === 'referent' ? 'bg-green-100 text-green-700' :
                      'bg-blue-100 text-blue-700'
                    }`}>
                      {invite.role === 'financeur' ? 'Financeur' : 
                       invite.role === 'referent' ? 'Référent' : 'Intervenant'}
                    </span>
                  </div>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                    invite.statut_confirmation === 'confirme' ? 'bg-emerald-100 text-emerald-700' :
                    invite.statut_confirmation === 'decline' ? 'bg-red-100 text-red-700' :
                    'bg-amber-100 text-amber-700'
                  }`}>
                    {invite.statut_confirmation === 'confirme' ? '✓ Confirmé' :
                     invite.statut_confirmation === 'decline' ? '✗ Décliné' :
                     '⏳ En attente'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminAteliersView;
